import { Component, useCallback } from 'react';
import type { ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import type React from 'react';

import type { Severity } from '@change-corgi/core/errorReporter/common';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

type BaseProps = PropsWithChildren<{
	readonly fallback: ReactNode;
	readonly onError: (error: unknown, errorInfo: ErrorInfo) => void;
}>;

type State = Readonly<{ hasError: boolean }>;

class ErrorBoundaryBase extends Component<BaseProps, State> {
	constructor(props: BaseProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
		const { onError } = this.props;
		onError(error, errorInfo);
	}

	render(): ReactNode {
		const { hasError } = this.state;
		const { fallback, children } = this.props;
		return hasError ? fallback : children;
	}
}

type Props = PropsWithChildren<{
	readonly fallback: ReactNode;
	readonly severity?: Severity;
}>;

/**
 * Catches errors thrown by its children — including errors decorated and rethrown by
 * `ErrorDecorator` — reports them once via the error reporter, and renders `fallback` in place
 * of the subtree. Unlike `ErrorDecorator`, this actually contains the error instead of
 * propagating it further up, so the rest of the page stays mounted.
 */
export const ErrorBoundary: React.FC<Props> = ({ fallback, severity = 'error', children }) => {
	const utilityContext = useUtilityContext();

	const handleError = useCallback(
		(error: unknown) => {
			const reportableError = error instanceof Error || typeof error === 'string' ? error : new Error(String(error));
			void utilityContext.errorReporter.report(reportableError, { severity });
		},
		[utilityContext, severity],
	);

	return (
		<ErrorBoundaryBase fallback={fallback} onError={handleError}>
			{children}
		</ErrorBoundaryBase>
	);
};
